<template>
  <div class="dashboard-preview-modal">
    <div class="dashboard-preview-modal__card">
      <header class="dashboard-preview-modal__header">
        <div class="dashboard-preview-modal__heading">
          <div class="dashboard-preview-modal__eyebrow-row">
            <p class="dashboard-preview-modal__eyebrow">Aperçu de l'actualité</p>
            <div v-if="contentUuid" class="dashboard-preview-modal__uuid-group">
              <button
                type="button"
                class="dashboard-preview-modal__uuid"
                :class="{
                  'dashboard-preview-modal__uuid--copied': uuidCopyStatus === 'copied',
                  'dashboard-preview-modal__uuid--error': uuidCopyStatus === 'error'
                }"
                :title="`Copier l'UUID complet : ${contentUuid}`"
                :aria-label="`Copier l'UUID complet ${contentUuid}`"
                @click="copyContentUuid"
              >
                <span>UUID: {{ truncatedUuid }}</span>
                <i
                  class="fas"
                  :class="uuidCopyStatus === 'copied' ? 'fa-check' : (uuidCopyStatus === 'error' ? 'fa-exclamation-circle' : 'fa-copy')"
                  aria-hidden="true"
                ></i>
              </button>
              <span
                class="dashboard-preview-modal__uuid-feedback"
                :class="{ 'dashboard-preview-modal__uuid-feedback--error': uuidCopyStatus === 'error' }"
                role="status"
                aria-live="polite"
              >{{ uuidCopyMessage }}</span>
            </div>
          </div>
          <h3 class="dashboard-preview-modal__title">{{ displayTitle }}</h3>
        </div>
        <button
          type="button"
          class="dashboard-preview-modal__close"
          @click="close"
          aria-label="Fermer l'aperçu"
        >
          <i class="fas fa-times"></i>
        </button>
      </header>

      <div class="dashboard-preview-modal__body">
        <div v-if="loading" class="dashboard-preview-modal__state">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Chargement de l'aperçu…</span>
        </div>

        <div v-else-if="error" class="dashboard-preview-modal__state dashboard-preview-modal__state--error">
          <i class="fas fa-triangle-exclamation"></i>
          <span>{{ error }}</span>
        </div>

        <div v-else-if="article" class="dashboard-preview-modal__content">
          <div v-if="thumbnailSrc" class="dashboard-preview-modal__thumbnail">
            <img :src="thumbnailSrc" :alt="`Illustration de ${displayTitle}`">
          </div>

          <ul class="dashboard-preview-modal__meta">
            <li v-if="publicationLabel">
              <i class="fas fa-calendar-alt"></i>
              <span>Publication prévue le {{ publicationLabel }}</span>
            </li>
            <li v-if="groupLabels.length">
              <i class="fas fa-users"></i>
              <span>{{ associatedUserGroupsLabel }}</span>
            </li>
          </ul>

          <div class="dashboard-preview-modal__rich-text" v-if="hasBody" v-html="article.body"></div>
          <p v-else class="dashboard-preview-modal__empty-body">Aucun contenu n'est disponible pour cette actualité.</p>

          <div v-if="fileLinks.length" class="dashboard-preview-modal__attachments">
            <h4>Pièces jointes</h4>
            <ul>
              <li v-for="file in fileLinks" :key="file.uuid || file.url">
                <i class="fas fa-paperclip"></i>
                <a :href="file.url" target="_blank" rel="noopener">{{ file.name }}</a>
              </li>
            </ul>
          </div>
        </div>

        <div v-else class="dashboard-preview-modal__state">
          <span>Aucune donnée à afficher.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns'
import { http } from '@/services/api.service'
import { resolveAssociatedUserGroupLabels } from '@/utils/associatedUserGroups'

export default {
  name: 'DashboardNewsPreviewModal',
  props: {
    articleUuid: {
      type: String,
      required: true
    },
    initialTitle: {
      type: String,
      default: ''
    },
    source: {
      type: String,
      default: 'pending'
    }
  },
  data () {
    return {
      loading: false,
      error: null,
      article: null,
      storageUrl: process.env.VUE_APP_STORAGE_URL,
      requestToken: 0,
      mavieproArticleCache: {},
      mavieproArticleList: null,
      uuidCopyStatus: 'idle',
      uuidCopyTimer: null
    }
  },
  computed: {
    applicationSlug () {
      const application = this.$store.getters.application
      return application?.app?.slug || null
    },
    displayTitle () {
      return this.article?.title || this.initialTitle || 'Actualité'
    },
    contentUuid () {
      const uuid = this.article?.uuid || this.articleUuid
      return uuid ? String(uuid) : ''
    },
    truncatedUuid () {
      return this.contentUuid.length > 16
        ? `${this.contentUuid.slice(0, 14)}…`
        : this.contentUuid
    },
    uuidCopyMessage () {
      if (this.uuidCopyStatus === 'copied') {
        return 'Copié'
      }
      if (this.uuidCopyStatus === 'error') {
        return 'Copie impossible'
      }
      return ''
    },
    hasBody () {
      const body = this.article?.body
      if (!body) {
        return false
      }
      if (typeof body === 'string') {
        return body.trim().length > 0
      }
      return Boolean(body)
    },
    groupLabels () {
      const notifications = Array.isArray(this.article?.notifications) ? this.article.notifications : []
      return resolveAssociatedUserGroupLabels(
        this.article?.group_labels,
        this.article?.groups,
        notifications.map(entry => entry?.group),
        notifications.map(entry => entry?.group_label)
      )
    },
    associatedUserGroupsLabel () {
      const prefix = this.groupLabels.length > 1 ? 'Groupes utilisateurs associés' : 'Groupe utilisateur associé'
      return `${prefix} : ${this.groupLabels.join(', ')}`
    },
    fileLinks () {
      const files = Array.isArray(this.article?.files) ? this.article.files : []
      return files
        .map(file => {
          const url = file?.url || null
          const name = file?.name || file?.filename || 'Document'
          if (!url) {
            return null
          }
          return {
            url,
            name,
            uuid: file?.uuid || null
          }
        })
        .filter(Boolean)
    },
    thumbnailSrc () {
      const directUrl = this.article?.thumbnail_url || this.article?.thumbnailUrl
      if (directUrl) {
        return directUrl
      }
      const thumbnail = this.article?.thumbnail
      if (!thumbnail || !this.storageUrl) {
        return null
      }
      return `${this.storageUrl}/thumbnails/${thumbnail}`
    },
    publicationLabel () {
      const publicationDate = this.article?.publication_date || this.article?.publicationDate
      const date = this.parseDate(publicationDate)
      if (!date) {
        return null
      }
      return format(date, 'dd/MM/yyyy HH:mm')
    }
  },
  watch: {
    articleUuid: {
      immediate: true,
      handler () {
        this.fetchArticle()
      }
    },
    source () {
      this.fetchArticle()
    },
    contentUuid () {
      this.resetUuidCopyStatus()
    }
  },
  beforeDestroy () {
    this.clearUuidCopyTimer()
  },
  methods: {
    isArticleVisibleForApplication (article) {
      const slug = this.applicationSlug
      if (!slug || !['maviepro', 'tract'].includes(slug)) {
        return true
      }
      const visibility = (article && article.media_mvp_visibility) || 'both'
      if (!visibility || visibility === 'both') {
        return true
      }
      return visibility === slug
    },
    filterByApplication (entries) {
      if (!Array.isArray(entries)) {
        return []
      }
      return entries.filter(entry => this.isArticleVisibleForApplication(entry))
    },
    parseDate (value) {
      if (!value) {
        return null
      }
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value
      }
      if (typeof value === 'string') {
        const normalized = value.replace(' ', 'T')
        const date = new Date(normalized)
        if (!Number.isNaN(date.getTime())) {
          return date
        }
      }
      return null
    },
    async fetchArticle () {
      if (!this.articleUuid) {
        this.article = null
        this.error = 'Identifiant de l\'actualité manquant.'
        return
      }
      const token = ++this.requestToken
      this.loading = true
      this.error = null
      this.article = null
      try {
        let data = null
        if (this.source === 'maviepro') {
          data = await this.fetchMavieproArticleByUuid(this.articleUuid)
        } else {
          const response = await http.get(`/article/${this.articleUuid}`)
          data = response && response.data ? response.data : null
        }
        if (this.requestToken !== token) {
          return
        }
        if (data) {
          this.article = data
        } else {
          this.article = null
          this.error = 'Aperçu indisponible pour cette actualité. Merci de la dupliquer pour la consulter.'
        }
      } catch (err) {
        if (this.requestToken !== token) {
          return
        }
        this.article = null
        this.error = this.source === 'maviepro'
          ? 'Impossible de récupérer cette actualité Maviepro pour le moment.'
          : 'Impossible de charger cette actualité pour le moment.'
      } finally {
        if (this.requestToken === token) {
          this.loading = false
        }
      }
    },
    async fetchMavieproArticleByUuid (uuid) {
      if (!uuid) {
        return null
      }

      if (this.mavieproArticleCache && this.mavieproArticleCache[uuid]) {
        return this.mavieproArticleCache[uuid]
      }

      let article = null

      try {
        const directResponse = await http.get(`/article/media-mvp/${uuid}`)
        if (directResponse && directResponse.data) {
          const candidate = directResponse.data
          if (this.isArticleVisibleForApplication(candidate)) {
            article = candidate
          }
        }
      } catch (error) {
        article = null
      }

      if (!article) {
        const list = await this.fetchMavieproArticleList()
        if (Array.isArray(list)) {
          article = list.find(entry => {
            if (!entry) {
              return false
            }
            const entryUuid = entry.uuid || entry.id
            if (!entryUuid) {
              return false
            }
            return String(entryUuid) === String(uuid)
          }) || null
        }
      }

      if (article) {
        this.$set(this.mavieproArticleCache, uuid, article)
      }

      return article || null
    },
    async fetchMavieproArticleList () {
      if (Array.isArray(this.mavieproArticleList) && this.mavieproArticleList.length) {
        return this.mavieproArticleList
      }

      try {
        const response = await http.get('/article/media-mvp')
        const payload = response && response.data ? response.data : null
        const entries = Array.isArray(payload)
          ? payload
          : (payload && Array.isArray(payload.items) ? payload.items : [])
        const filteredEntries = this.filterByApplication(entries)
        this.mavieproArticleList = filteredEntries
        return filteredEntries
      } catch (error) {
        this.mavieproArticleList = []
        throw error
      }
    },
    async copyContentUuid () {
      if (!this.contentUuid) {
        return
      }

      try {
        if (navigator.clipboard && window.isSecureContext) {
          try {
            await navigator.clipboard.writeText(this.contentUuid)
          } catch (error) {
            this.copyWithFallback(this.contentUuid)
          }
        } else {
          this.copyWithFallback(this.contentUuid)
        }
        this.setUuidCopyStatus('copied')
      } catch (error) {
        this.setUuidCopyStatus('error')
      }
    },
    copyWithFallback (value) {
      const textarea = document.createElement('textarea')
      textarea.value = value
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      let copied = false
      try {
        textarea.select()
        copied = document.execCommand('copy')
      } finally {
        document.body.removeChild(textarea)
      }
      if (!copied) {
        throw new Error('Clipboard copy failed')
      }
    },
    setUuidCopyStatus (status) {
      this.clearUuidCopyTimer()
      this.uuidCopyStatus = status
      this.uuidCopyTimer = window.setTimeout(() => {
        this.uuidCopyStatus = 'idle'
        this.uuidCopyTimer = null
      }, 2500)
    },
    resetUuidCopyStatus () {
      this.clearUuidCopyTimer()
      this.uuidCopyStatus = 'idle'
    },
    clearUuidCopyTimer () {
      if (this.uuidCopyTimer !== null) {
        window.clearTimeout(this.uuidCopyTimer)
        this.uuidCopyTimer = null
      }
    },
    close () {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.dashboard-preview-modal {
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 24px;
  box-sizing: border-box;
}

.dashboard-preview-modal__card {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 26px 48px -32px rgba(15, 23, 42, 0.35);
  border: 1px solid rgba(17, 24, 39, 0.08);
  width: 100%;
  max-width: 840px;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dashboard-preview-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 28px 16px;
  gap: 16px;
  background: linear-gradient(135deg, rgba(58, 123, 213, 0.1), rgba(58, 123, 213, 0.02));
}

.dashboard-preview-modal__heading {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.dashboard-preview-modal__eyebrow {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #2563eb;
}

.dashboard-preview-modal__title {
  margin: 0;
  font-size: 1.5rem;
  line-height: 1.25;
  color: #111827;
  font-weight: 600;
}

.dashboard-preview-modal__eyebrow-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px 14px;
}

.dashboard-preview-modal__uuid-group {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.dashboard-preview-modal__uuid {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 9px;
  border: 1px solid rgba(37, 99, 235, 0.22);
  border-radius: 8px;
  background: rgba(37, 99, 235, 0.08);
  color: #1d4ed8;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.dashboard-preview-modal__uuid:hover,
.dashboard-preview-modal__uuid:focus-visible {
  border-color: rgba(37, 99, 235, 0.48);
  background: rgba(37, 99, 235, 0.14);
}

.dashboard-preview-modal__uuid:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.dashboard-preview-modal__uuid--copied {
  border-color: rgba(5, 150, 105, 0.32);
  background: rgba(5, 150, 105, 0.1);
  color: #047857;
}

.dashboard-preview-modal__uuid--error {
  border-color: rgba(220, 38, 38, 0.32);
  background: rgba(220, 38, 38, 0.08);
  color: #b91c1c;
}

.dashboard-preview-modal__uuid-feedback {
  min-width: 36px;
  color: #047857;
  font-size: 0.72rem;
  font-weight: 600;
}

.dashboard-preview-modal__uuid-feedback--error {
  color: #b91c1c;
}

.dashboard-preview-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: none;
  background: rgba(37, 99, 235, 0.12);
  color: #2563eb;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.2s ease;
}

.dashboard-preview-modal__close:hover {
  transform: translateY(-1px);
  background: rgba(37, 99, 235, 0.18);
}

.dashboard-preview-modal__body {
  flex: 1 1 auto;
  padding: 24px 28px 32px;
  overflow-y: auto;
  display: flex;
}

.dashboard-preview-modal__content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.dashboard-preview-modal__state {
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  font-size: 0.95rem;
  color: #6b7280;
  text-align: center;
}

.dashboard-preview-modal__state--error {
  color: #dc2626;
}

.dashboard-preview-modal__thumbnail img {
  width: 100%;
  max-height: 240px;
  object-fit: cover;
  border-radius: 16px;
}

.dashboard-preview-modal__meta {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 0.9rem;
  color: #4b5563;
}

.dashboard-preview-modal__meta i {
  margin-right: 8px;
  color: #2563eb;
}

.dashboard-preview-modal__rich-text {
  font-size: 1rem;
  line-height: 1.6;
  color: #1f2937;
}

.dashboard-preview-modal__rich-text :deep(img) {
  max-width: 100%;
  border-radius: 12px;
}

.dashboard-preview-modal__rich-text :deep(h1),
.dashboard-preview-modal__rich-text :deep(h2),
.dashboard-preview-modal__rich-text :deep(h3) {
  margin-top: 1.5em;
  margin-bottom: 0.75em;
  color: #111827;
}

.dashboard-preview-modal__rich-text :deep(p) {
  margin-bottom: 1em;
}

.dashboard-preview-modal__empty-body {
  font-size: 0.95rem;
  color: #6b7280;
}

.dashboard-preview-modal__attachments {
  background: rgba(59, 130, 246, 0.06);
  border-radius: 14px;
  padding: 16px 18px;
}

.dashboard-preview-modal__attachments h4 {
  margin: 0 0 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1d4ed8;
}

.dashboard-preview-modal__attachments ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dashboard-preview-modal__attachments li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.dashboard-preview-modal__attachments a {
  color: #1d4ed8;
  text-decoration: underline;
}

.dashboard-preview-modal__attachments i {
  color: #1d4ed8;
}

@media (max-width: 640px) {
  .dashboard-preview-modal__card {
    border-radius: 16px;
  }

  .dashboard-preview-modal__header,
  .dashboard-preview-modal__body {
    padding: 20px;
  }
}
</style>
